import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from '@/components/ui/use-toast';
import { findRegenerationJob, readRegenerationJob, RegenerationJob } from '@/services/contentReviewService';

export type RegenerationObservation = { contentId: string; requestId?: string; job?: RegenerationJob; rejected?: boolean };
type Watch = RegenerationObservation & { controller?: AbortController; timer?: ReturnType<typeof setTimeout>; until: number };
const WINDOW_MS = 300000;

/** Queue invalidation outlives the Sheet's item-specific control. Only observes existing requests. */
export function useRegenerationQueueObserver(userId: string, siteId: string, onComplete: () => void) {
  const scope = useMemo(() => ({ watches: new Map<string, Watch>(), completed: new Set<string>() }), [userId, siteId]);
  const live = useRef<typeof scope | null>(scope);
  live.current = scope;
  const completion = useRef(onComplete);
  completion.current = onComplete;

  function stop(watch: Watch) {
    clearTimeout(watch.timer);
    watch.controller?.abort();
    watch.controller = undefined;
  }
  function accept(watch: Watch, job: RegenerationJob) {
    if (live.current !== scope) return;
    watch.job = job;
    if (job.status !== 'completed' && job.status !== 'failed') return;
    stop(watch);
    scope.watches.delete(watch.contentId);
    if (job.status === 'completed' && !scope.completed.has(job.jobId)) {
      scope.completed.add(job.jobId);
      completion.current();
    }
  }
  function poll(watch: Watch) {
    if (watch.controller || live.current !== scope) return;
    const own = new AbortController();
    watch.controller = own;
    const tick = async () => {
      if (own.signal.aborted || live.current !== scope) return;
      try {
        const job = watch.job
          ? await readRegenerationJob(watch.job.jobId, own.signal)
          : await findRegenerationJob(watch.contentId, watch.requestId, own.signal);
        if (own.signal.aborted || live.current !== scope) return;
        if (job) accept(watch, job);
        if (own.signal.aborted) return;
        if (Date.now() >= watch.until) throw new Error('Automatic queue status checks paused. Return to this window or check the existing request to resume.');
        watch.timer = setTimeout(tick, 3000);
      } catch (error) {
        if (own.signal.aborted || live.current !== scope) return;
        stop(watch); // Keep the receipt for focus/online/manual reconnection; never POST here.
        toast({ title: 'Regeneration status unavailable', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
      }
    };
    watch.timer = setTimeout(tick, 3000);
  }

  const track = useCallback((observation: RegenerationObservation) => {
    if (live.current !== scope || !userId || !siteId) return;
    if (observation.job && scope.completed.has(observation.job.jobId)) return;
    let watch = scope.watches.get(observation.contentId);
    if (observation.rejected) {
      if (watch && watch.requestId === observation.requestId) {
        stop(watch);
        scope.watches.delete(watch.contentId);
      }
      return;
    }
    if (watch && ((observation.requestId && watch.requestId && observation.requestId !== watch.requestId)
      || (observation.job && watch.job && observation.job.jobId !== watch.job.jobId))) {
      stop(watch);
      watch = undefined;
    }
    if (!watch) {
      watch = { ...observation, until: Date.now() + WINDOW_MS };
      scope.watches.set(observation.contentId, watch);
    } else if (observation.requestId) watch.requestId = observation.requestId;
    if (observation.job) accept(watch, observation.job);
    if (scope.watches.has(observation.contentId)) poll(watch);
    // Functions close over this immutable user/site scope; completion is read from a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, userId, siteId]);

  useEffect(() => {
    live.current = scope;
    const resume = () => {
      for (const watch of scope.watches.values()) {
        if (!watch.controller) { watch.until = Date.now() + WINDOW_MS; poll(watch); }
      }
    };
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    return () => {
      if (live.current === scope) live.current = null;
      for (const watch of scope.watches.values()) stop(watch);
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  return track;
}
